import { UAContext } from '@quentin-sommer/react-useragent';
import cn from 'classnames';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { useWindowScroll } from 'react-use';
import Typography from '~components/typography';
import { Trans, useTranslation } from '~i18n';

interface IBookingReminderProps {
  bookings: any;
}

const BookingReminder: React.FC<IBookingReminderProps & React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  bookings,
}) => {
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { t } = useTranslation(['student_portal']);
  const [isShow, setIsShow] = useState(false);
  const { y } = useWindowScroll();
  const heldBooking = bookings.heldBooking;
  const pendingBooking = bookings.pendingBooking;

  return (
    <div
      className={cn('booking-reminder', className, {
        'booking-reminder--hidden': y > 140 && !isShow,
      })}
      onMouseEnter={() => {
        setIsShow(true);
      }}
      onMouseLeave={() => {
        setIsShow(false);
      }}
      onClick={() => {
        if (isMobile) {
          setIsShow(false);
        }
      }}
    >
      <div className="booking-reminder__clock">
        <div className="booking-reminder__spinner"></div>
        <div className="booking-reminder__hourhand"></div>
        <div className="booking-reminder__dot"></div>
      </div>
      {heldBooking.length > 0 && (
        <div className="booking-reminder__content">
          <Typography variant={isMobile ? 'meta' : 'h9'}>
            <Trans i18nKey="my_bookings.reminder">
              {t('you_have_reservations_expiring_on')}
              <Typography
                component="span"
                variant={isMobile ? 'meta' : 'h9'}
                fontWeight={600}
                color="var(--color-red_6)"
              >
                {{ date: ` ${moment(heldBooking[0].heldToTime).format('lll')} ` }}
              </Typography>
            </Trans>
            {t('at_the_earliest')}
          </Typography>
        </div>
      )}
      {pendingBooking.length > 0 && heldBooking.length === 0 && (
        <div className="booking-reminder__content">
          <Typography variant={isMobile ? 'meta' : 'h9'}>
            <Trans i18nKey="my_bookings.reminder.pending">
              {t('you_have')}
              <Typography
                component="span"
                variant={isMobile ? 'meta' : 'h9'}
                color="var(--color-red_6)"
                fontWeight={600}
              >
                {{ number: ` ${pendingBooking.length} ` }}
              </Typography>
              {t('bookings_expiring_within')}
              <Typography
                component="span"
                variant={isMobile ? 'meta' : 'h9'}
                color="var(--color-red_6)"
                fontWeight={600}
              >
                {{
                  date: ` ${moment(pendingBooking[0].countDown).format('lll')} `,
                }}
              </Typography>
            </Trans>
            {t('at_the_earliest')}
          </Typography>
        </div>
      )}

      <style jsx>{`
        .booking-reminder {
          width: 380px;
          height: 80px;
          background: var(--color-red_2);
          box-shadow: 0 2px 8px 0 rgba(76, 76, 76, 0.16);
          border-radius: 100px 0 0 100px;
          position: fixed;
          top: 138px;
          right: 0;
          z-index: ${1000};
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: right 0.5s;
          &--hidden {
            right: -290px;
            background-color: var(--color-red_6);

            .booking-reminder__spinner,
            .booking-reminder__hourhand,
            .booking-reminder__dot {
              background: var(--color-white);
            }
            .booking-reminder__content {
              visibility: hidden;
            }
          }
          &__content {
            height: 80px;
            flex: 1;
            display: flex;
            align-items: center;
          }
          &__clock {
            width: 80px;
            height: 80px;
            align-items: center;
            display: flex;
            justify-content: center;
            margin-left: 10px;
            margin-right: 10px;
          }
          &__spinner {
            animation: spinnerTwo 4s linear infinite;
            background: var(--color-red_6);
            height: 30px;
            border-radius: 100px;
            transform-origin: top;
            position: absolute;
            top: 50%;
            width: 3px;
          }
          &__hourhand {
            animation: spinnerTwo 18s linear infinite;
            background: var(--color-red_6);
            border-radius: 100px;
            height: 20px;
            transform-origin: top;
            position: absolute;
            top: 50%;
            width: 4px;
          }
          &__dot {
            background: var(--color-red_6);
            border-radius: 100%;
            height: 8px;
            width: 8px;
          }
        }
        @media screen and (max-width: 768px) {
          .booking-reminder {
            width: 300px;
            height: 64px;
            position: fixed;
            top: unset;
            bottom: 16px;
            right: 0;
            &--hidden {
              right: -230px;
            }
            &__content {
              height: 64px;
              padding-right: 4px;
            }
            &__clock {
              width: 64px;
              height: 64px;
              margin-left: 4px;
              margin-right: 4px;
            }
            &__spinner {
              height: 24px;
            }
            &__hourhand {
              height: 13px;
            }
            &__dot {
              height: 6px;
              width: 6px;
            }
          }
        }
        @keyframes spinnerTwo {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default BookingReminder;
